import { useTranslation } from 'react-i18next';

import type { ExceptionPolicy, RecurrencePreview } from '@/entities/recurrence';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { Button, Dialog } from '@/shared/ui';

import styles from './Recurrence.module.css';

export interface PolicyConflict {
  original_date: string;
  reason: string;
}

interface Props {
  /** How many upcoming individual changes the series has. */
  count: number;
  /** Where the changes would go under the new schedule (undefined while it is being computed). */
  preview: RecurrencePreview | undefined;
  /** Set once the server refused "preserve" as ambiguous. */
  conflicts: readonly PolicyConflict[] | null;
  pending: boolean;
  onChoose: (policy: ExceptionPolicy) => void;
  /** Keep the changes, but reset these dates (the conflicting ones). */
  onResolve: (dates: string[]) => void;
  onCancel: () => void;
}

/**
 * Editing a whole series that has individual changes ahead: the person decides explicitly to keep
 * them (moved onto the new slots) or to reset them. An ambiguous mapping is explained and can be
 * resolved by resetting just the conflicting changes; cancelling changes nothing.
 */
export function ExceptionPolicyDialog({
  count,
  preview,
  conflicts,
  pending,
  onChoose,
  onResolve,
  onCancel,
}: Props) {
  const { t, i18n } = useTranslation();
  const show = (day: string) => formatCalendarDate(day, i18n.language);

  if (conflicts && conflicts.length > 0) {
    return (
      <Dialog title={t('recurrence.conflict.title')} onClose={onCancel}>
        <div className={styles.policy} data-testid="exception-conflicts">
          <p className={styles.note}>{t('recurrence.conflict.message')}</p>
          <ul className={styles.list}>
            {conflicts.map((conflict) => (
              <li key={conflict.original_date}>
                <strong>{show(conflict.original_date)}</strong>:{' '}
                {t(`recurrence.conflict.reason.${conflict.reason}`, {
                  defaultValue: conflict.reason,
                })}
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={pending}
              onClick={() => {
                onResolve(conflicts.map((conflict) => conflict.original_date));
              }}
            >
              {t('recurrence.conflict.resetAndSave')}
            </Button>
            <Button onClick={onCancel}>{t('recurrence.conflict.back')}</Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={t('recurrence.policy.title')} onClose={onCancel}>
      <div className={styles.policy} data-testid="exception-policy">
        <p className={styles.note}>{t('recurrence.policy.message', { count })}</p>
        {preview && preview.mapped.length > 0 && (
          <ul className={styles.list}>
            {preview.mapped.map((move) => (
              <li key={move.original_date}>
                {show(move.original_date)} → {show(move.new_date)}
              </li>
            ))}
          </ul>
        )}
        {preview && preview.conflicts.length > 0 && (
          <p className={styles.warn} role="status">
            {t('recurrence.conflict.message')}
          </p>
        )}
        <div className={styles.choices}>
          <Button
            variant="primary"
            disabled={pending}
            onClick={() => {
              onChoose('preserve');
            }}
          >
            {t('recurrence.policy.preserve')}
          </Button>
          <p className={styles.note}>{t('recurrence.policy.preserveHint')}</p>
          <Button
            disabled={pending}
            onClick={() => {
              onChoose('reset');
            }}
          >
            {t('recurrence.policy.reset')}
          </Button>
          <p className={styles.note}>{t('recurrence.policy.resetHint')}</p>
        </div>
        <div className={styles.actions}>
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
